import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IAdminSupplier, IEarningPeriod, IPartnerAdmin } from 'types/income';
import { TableDataResponse } from 'types/general';
import {
  AdminSupplier,
  AdminSuppliersFilters,
  CreatePartnerAdmin,
  UpdatePartnerAdmin,
} from './admin-suppliers.types';

interface GetSupplier {
  supplierId: string;
}

interface GetSuppliers {
  filters: Partial<AdminSuppliersFilters>;
}

interface GetSuppliersStatistics {
  filters: Partial<AdminSuppliersFilters>;
}

interface CreateSupplier {
  data: Pick<
    AdminSupplier,
    | 'name'
    | 'status'
    | 'logo'
    | 'description'
    | 'additionalInfo'
    | 'termsAndConditionsLink'
  >;
}

interface UpdateSupplier {
  supplierId: string;
  data: Partial<CreateSupplier['data']> & { earningFeeBps?: number };
}

interface DeleteSupplier {
  supplierId: string;
}

interface EnablePartnerPortal {
  supplierId: string;
}

interface OpenPartnerPortal {
  supplierId: string;
}

interface DisablePartnerPortal {
  supplierId: string;
}

interface GetPartnerAdmins {
  supplierId: string;
}

interface GetPartnerAdmin {
  supplierId: string;
  adminId: string;
}

interface CreatePartnerAdminPayload {
  supplierId: string;
  data: CreatePartnerAdmin;
}

interface UpdatePartnerAdminPayload {
  supplierId: string;
  adminId: string;
  data: UpdatePartnerAdmin;
}

interface DeletePartnerAdmin {
  supplierId: string;
  adminId: string;
}

interface LinkPartnerAdminCognito {
  supplierId: string;
  adminId: string;
}

interface GetEarningPeriods {
  supplierId: string;
}

interface OpenEarningPeriod {
  supplierId: string;
}

interface CloseEarningPeriod {
  supplierId: string;
}

export const loaders = {
  getSupplier: ({ supplierId }: GetSupplier) => {
    return api<IAdminSupplier>({
      url: Endpoints.melpAdmin.suppliers.byId.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
    });
  },
  getSuppliers: ({ filters }: GetSuppliers) => {
    return api<TableDataResponse<IAdminSupplier>>({
      url: Endpoints.melpAdmin.suppliers.root,
      context: 'melp',
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        filter: {
          name: filters.name || undefined,
          systemName: filters.systemName || undefined,
          status: filters.status,
        },
      },
    });
  },
  getSuppliersStatistics: ({ filters }: GetSuppliersStatistics) => {
    return api<TableDataResponse<IAdminSupplier>>({
      url: Endpoints.melpAdmin.suppliers.statistics,
      context: 'melp',
      params: {
        filter: {
          name: filters.name || undefined,
          systemName: filters.systemName || undefined,
          status: filters.status,
        },
      },
    });
  },
  createSupplier: ({ data: { logo, ...data } }: CreateSupplier) => {
    return api<IAdminSupplier>({
      url: Endpoints.melpAdmin.suppliers.root,
      context: 'melp',
      method: 'POST',
      data: {
        ...data,
        logoId: logo,
      },
    });
  },
  updateSupplier: ({ supplierId, data: { logo, ...data } }: UpdateSupplier) => {
    return api<IAdminSupplier>({
      url: Endpoints.melpAdmin.suppliers.byId.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'PUT',
      data: {
        ...data,
        ...(logo !== undefined ? { logoId: logo } : {}),
      },
    });
  },
  deleteSupplier: ({ supplierId }: DeleteSupplier) => {
    return api({
      url: Endpoints.melpAdmin.suppliers.byId.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'DELETE',
    });
  },
  enablePartnerPortal: ({ supplierId }: EnablePartnerPortal) => {
    return api({
      url: Endpoints.melpAdmin.suppliers.enablePartnerPortal.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'PATCH',
      data: {},
    });
  },
  openPartnerPortal: ({ supplierId }: OpenPartnerPortal) => {
    return api<{ url: string }>({
      url: Endpoints.melpAdmin.suppliers.openPartnerPortal.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'POST',
    });
  },
  disablePartnerPortal: ({ supplierId }: DisablePartnerPortal) => {
    return api({
      url: Endpoints.melpAdmin.suppliers.disablePartnerPortal.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'PATCH',
    });
  },
  getPartnerAdmins: ({ supplierId }: GetPartnerAdmins) => {
    return api<{ data: IPartnerAdmin[] }>({
      url: Endpoints.melpAdmin.suppliers.admins.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
    });
  },
  getPartnerAdmin: ({ supplierId, adminId }: GetPartnerAdmin) => {
    return api<IPartnerAdmin>({
      url: Endpoints.melpAdmin.suppliers.adminById
        .replace(':supplierId', supplierId)
        .replace(':adminId', adminId),
      context: 'melp',
    });
  },
  createPartnerAdmin: ({ supplierId, data }: CreatePartnerAdminPayload) => {
    return api<IPartnerAdmin>({
      url: Endpoints.melpAdmin.suppliers.admins.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'POST',
      data,
    });
  },
  updatePartnerAdmin: ({
    supplierId,
    adminId,
    data,
  }: UpdatePartnerAdminPayload) => {
    return api<IPartnerAdmin>({
      url: Endpoints.melpAdmin.suppliers.adminById
        .replace(':supplierId', supplierId)
        .replace(':adminId', adminId),
      context: 'melp',
      method: 'PATCH',
      data,
    });
  },
  deletePartnerAdmin: ({ supplierId, adminId }: DeletePartnerAdmin) => {
    return api({
      url: Endpoints.melpAdmin.suppliers.adminById
        .replace(':supplierId', supplierId)
        .replace(':adminId', adminId),
      context: 'melp',
      method: 'DELETE',
    });
  },
  linkPartnerAdminCognito: ({
    supplierId,
    adminId,
  }: LinkPartnerAdminCognito) => {
    return api<IPartnerAdmin>({
      url: Endpoints.melpAdmin.suppliers.linkPartnerAdminCognito
        .replace(':supplierId', supplierId)
        .replace(':adminId', adminId),
      context: 'melp',
      method: 'PATCH',
    });
  },
  createPartnerAdminCognito: ({
    supplierId,
    adminId,
  }: LinkPartnerAdminCognito) => {
    return api<IPartnerAdmin>({
      url: Endpoints.melpAdmin.suppliers.createPartnerAdminCognito
        .replace(':supplierId', supplierId)
        .replace(':adminId', adminId),
      context: 'melp',
      method: 'PATCH',
    });
  },
  getEarningPeriods: ({ supplierId }: GetEarningPeriods) => {
    return api<IEarningPeriod[]>({
      url: Endpoints.melpAdmin.suppliers.earningPeriods.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
    });
  },
  openEarningPeriod: ({ supplierId }: OpenEarningPeriod) => {
    return api<IEarningPeriod>({
      url: Endpoints.melpAdmin.suppliers.earningPeriods.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'POST',
      data: {},
    });
  },
  closeEarningPeriod: ({ supplierId }: CloseEarningPeriod) => {
    return api<IEarningPeriod>({
      url: Endpoints.melpAdmin.suppliers.closeEarningPeriod.replace(
        ':supplierId',
        supplierId,
      ),
      context: 'melp',
      method: 'POST',
      data: {},
    });
  },
};
